import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FeedItem, SurveyFeedItem } from '../../models/data';
import { feedItemService } from '../../services';
import { commonActions } from '../commonActions';
import { FeedState } from './types';

const getFeedItems = createAsyncThunk('feedItems/getFeedItems', async () => {
  return feedItemService.getFeedItems();
});

const completeSurvey = createAsyncThunk('feedItems/completeSurvey', async (surveyId: string) => {
  await feedItemService.completeSurvey(surveyId);
  return surveyId;
});

const initialState: FeedState = {
  feedItems: [],
  surveyFeedItems: [],
  loading: false,
  error: null,
};

export const feedSlice = createSlice({
  name: 'feed',
  initialState,
  reducers: {
    updateLoading(state, { payload: { loading } }: PayloadAction<{ loading: boolean }>) {
      state.loading = loading;
    },

    setFeedItems(state, { payload: { feedItems } }: PayloadAction<{ feedItems: FeedItem[] }>) {
      state.feedItems = feedItems;
    },

    setSurveyFeedItems(
      state,
      { payload: { surveyFeedItems } }: PayloadAction<{ surveyFeedItems: SurveyFeedItem[] }>
    ) {
      state.surveyFeedItems = surveyFeedItems;
    },

    updateError(state, { payload: { error } }: PayloadAction<{ error: string | null }>) {
      state.error = error;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFeedItems.pending, (state) => {
        state.loading = true;
      })
      .addCase(getFeedItems.fulfilled, (state, action) => {
        state.loading = false;
        state.feedItems = action.payload?.feedItems ?? [];
        state.surveyFeedItems = action.payload?.surveyFeedItems ?? [];
      })
      .addCase(getFeedItems.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message ?? 'Unable to load feed.';
      });

    builder
      .addCase(completeSurvey.fulfilled, (state, action) => {
        state.surveyFeedItems = state.surveyFeedItems.filter((s) => s.id !== action.payload);
      })
      .addCase(completeSurvey.rejected, (state, action) => {
        state.error = action.error.message ?? 'Unable to mark survey as complete.';
      });

    builder.addCase(commonActions.clearCache.fulfilled, () => {
      return { ...initialState };
    });
  },
});

export const feedActions = { ...feedSlice.actions, getFeedItems, completeSurvey };

export type FeedItemSlice = {
  [feedSlice.name]: ReturnType<typeof feedSlice['reducer']>;
};
